import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Search, BatteryCharging, PlusCircle, Pencil, Trash2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import DynamicFields from "@/components/DynamicFields";
import { useAuth } from "@/context/AuthContext";
import { REGISTRAR_ROLES } from "@/lib/constants";
import api from "@/lib/apiClient";

export default function Batteries() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(null);
  const [custom, setCustom] = useState({});
  const canManage = REGISTRAR_ROLES.includes(user?.role);

  const load = useCallback(async (query = "") => {
    setLoading(true);
    try {
      const res = await api.get("/batteries", { params: query ? { q: query } : {} });
      setRows(res.data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openEdit = (b) => { setEdit({ ...b }); setCustom(b.custom || {}); };
  const setEditField = (k) => (e) => setEdit((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const saveEdit = async () => {
    try {
      await api.put(`/batteries/${edit.id}`, {
        model: edit.model, capacity: edit.capacity, purchase_date: edit.purchase_date,
        warranty_months: Number(edit.warranty_months), customer_name: edit.customer_name,
        customer_phone: edit.customer_phone, customer_email: edit.customer_email,
        customer_city: edit.customer_city, customer_pincode: edit.customer_pincode,
        channel: edit.channel, invoice_no: edit.invoice_no, notes: edit.notes, custom,
      });
      toast.success("Battery updated.");
      setEdit(null);
      load(q);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed."); }
  };

  const del = async (id) => {
    try { await api.delete(`/batteries/${id}`); toast.success("Battery deleted."); load(q); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed."); }
  };

  return (
    <div className="space-y-6" data-testid="batteries-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-extrabold">Batteries</h1>
          <p className="text-muted-foreground mt-1">Registered lithium batteries and their warranty coverage.</p>
        </div>
        {canManage && (
          <Button onClick={() => navigate("/register-battery")} className="gap-2" data-testid="register-battery-cta">
            <PlusCircle className="h-4 w-4" /> Register Battery
          </Button>
        )}
      </div>

      <div className="flex gap-2 max-w-md">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            data-testid="battery-search-input"
            placeholder="Search serial, customer or phone..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load(q)}
            className="pl-9"
          />
        </div>
        <Button variant="secondary" onClick={() => load(q)} data-testid="battery-search-button">Search</Button>
      </div>

      <Card className="overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/50">
              <TableHead>Serial</TableHead>
              <TableHead>Model</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>Purchased</TableHead>
              <TableHead>Warranty Ends</TableHead>
              <TableHead>Channel</TableHead>
              {canManage && <TableHead className="text-right">Actions</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={7} className="text-center py-10 text-muted-foreground">Loading...</TableCell></TableRow>
            ) : rows.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center py-12 text-muted-foreground">
                <BatteryCharging className="h-10 w-10 mx-auto mb-2 opacity-40" />No batteries found.
              </TableCell></TableRow>
            ) : (
              rows.map((b) => (
                <TableRow key={b.id} data-testid={`battery-row-${b.serial_number}`} className="hover:bg-accent/40">
                  <TableCell className="font-mono font-semibold">{b.serial_number}</TableCell>
                  <TableCell>{b.model}<div className="text-xs text-muted-foreground">{b.capacity}</div></TableCell>
                  <TableCell>{b.customer_name}<div className="text-xs text-muted-foreground">{b.customer_phone}</div></TableCell>
                  <TableCell>{b.purchase_date}</TableCell>
                  <TableCell>{b.warranty_end || "—"}</TableCell>
                  <TableCell><span className="text-xs font-semibold px-2 py-0.5 rounded bg-secondary">{b.channel}</span></TableCell>
                  {canManage && (
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="icon" onClick={() => openEdit(b)} data-testid={`edit-battery-${b.serial_number}`}><Pencil className="h-4 w-4" /></Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="icon" className="text-red-600" data-testid={`delete-battery-${b.serial_number}`}><Trash2 className="h-4 w-4" /></Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader><AlertDialogTitle>Delete battery {b.serial_number}?</AlertDialogTitle>
                              <AlertDialogDescription>This permanently removes the battery record.</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => del(b.id)} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="edit-battery-dialog">
          <DialogHeader><DialogTitle className="font-display">Edit Battery {edit?.serial_number}</DialogTitle>
            <DialogDescription>Update battery and customer details.</DialogDescription>
          </DialogHeader>
          {edit && (
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Model</Label><Input value={edit.model || ""} onChange={setEditField("model")} data-testid="edit-model-input" /></div>
              <div className="space-y-1.5"><Label>Capacity</Label><Input value={edit.capacity || ""} onChange={setEditField("capacity")} /></div>
              <div className="space-y-1.5"><Label>Purchase Date</Label><Input type="date" value={edit.purchase_date || ""} onChange={setEditField("purchase_date")} /></div>
              <div className="space-y-1.5"><Label>Warranty (months)</Label><Input type="number" value={edit.warranty_months || 36} onChange={setEditField("warranty_months")} /></div>
              <div className="space-y-1.5"><Label>Customer Name</Label><Input value={edit.customer_name || ""} onChange={setEditField("customer_name")} data-testid="edit-customer-input" /></div>
              <div className="space-y-1.5"><Label>Phone</Label><Input value={edit.customer_phone || ""} onChange={setEditField("customer_phone")} /></div>
              <div className="space-y-1.5"><Label>City</Label><Input value={edit.customer_city || ""} onChange={setEditField("customer_city")} /></div>
              <div className="space-y-1.5"><Label>Pincode</Label><Input value={edit.customer_pincode || ""} onChange={setEditField("customer_pincode")} /></div>
              <div className="space-y-1.5"><Label>Invoice No.</Label><Input value={edit.invoice_no || ""} onChange={setEditField("invoice_no")} /></div>
              <div className="space-y-1.5"><Label>Channel</Label>
                <Select value={edit.channel || "B2C"} onValueChange={setEditField("channel")}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="B2C">B2C</SelectItem><SelectItem value="B2B">B2B</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="col-span-2"><DynamicFields entity="battery" values={custom} onChange={(k, v) => setCustom((c) => ({ ...c, [k]: v }))} /></div>
            </div>
          )}
          <DialogFooter><Button onClick={saveEdit} data-testid="save-battery-edit">Save Changes</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
